import { useState, useEffect, useCallback, useRef } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import { __ } from '@wordpress/i18n';
import { registerCheckoutBlock } from '@woocommerce/blocks-checkout';
import metadata from './block.json';

const closeWidget = () => {
	document.querySelectorAll( '.method-detail' ).forEach( ( el ) => {
		el.innerText = '';
		el.style.height = '0';
	} );
	try {
		if ( typeof Packeta !== 'undefined' && Packeta.Widget ) {
			Packeta.Widget.close();
		}
	} catch ( e ) {}
};

const getSelectedMethodId = ( cartStore ) => {
	try {
		if ( ! cartStore || typeof cartStore.getCartData !== 'function' ) return '';
		const data = cartStore.getCartData();
		if ( ! data?.shippingRates?.[ 0 ] ) return '';
		const rates = data.shippingRates[ 0 ].shipping_rates || [];
		const selected = rates.find( ( r ) => r.selected === true );
		return selected?.method_id || '';
	} catch {
		return '';
	}
};

const AddressDisplay = ( { label, value } ) => {
	const hasValue = !! ( value && value.trim() );
	return (
		<div className={ `wc-block-components-text-input tzas-data${ hasValue ? ' is-active' : '' }` }>
			<input
				type="text"
				readOnly
				tabIndex={ -1 }
				value={ value }
				aria-label={ label }
				onChange={ () => {} }
			/>
			<label>{ label }</label>
		</div>
	);
};

const ParcelshopsComponent = ( { children, checkoutExtensionData } ) => {
	const [ pointData, setPointData ]               = useState( '' );
	const [ selectedText, setSelectedText ]         = useState( '' );
	const [ showError, setShowError ]               = useState( false );
	const { setExtensionData }                      = checkoutExtensionData;
	const [ widgetLocked, setWidgetLocked ]         = useState( false );
	const [ hasAttemptedOpen, setHasAttemptedOpen ] = useState( false );
	const prevRateId                                = useRef( null );
	const placeOrderBtnRef                          = useRef( null );
	const errorRef                                  = useRef( null );

	const shippingState = useSelect( ( select ) => {
		try {
			const store = select( 'wc/store/cart' );
			if ( ! store ) return { method_id: '', rate_id: '', hasBranches: false, belongsToZasilkovna: false };
			const data = store.getCartData();
			if ( ! data?.shippingRates?.[ 0 ]?.shipping_rates ) return { method_id: '', rate_id: '', hasBranches: false, belongsToZasilkovna: false };
			const rate                = data.shippingRates[ 0 ].shipping_rates.find( ( r ) => r.selected === true );
			const hasBranches         = rate?.meta_data?.find( ( m ) => m.key === 'has_branches' )?.value === '1';
			const methodId            = rate?.method_id || '';
			const rateId              = rate?.rate_id || '';
			const belongsToZasilkovna = String( rateId || methodId ).startsWith( 'zasilkovna>' );
			return { method_id: methodId, rate_id: rateId, hasBranches: Boolean( hasBranches ), belongsToZasilkovna };
		} catch {
			return { method_id: '', rate_id: '', hasBranches: false, belongsToZasilkovna: false };
		}
	} );

	const customerState = useSelect( ( select ) => {
		try {
			const store = select( 'wc/store/cart' );
			if ( ! store ) return { country: '' };
			const customer = store.getCustomerData();
			return { country: customer?.shippingAddress?.country || customer?.billingAddress?.country || '' };
		} catch {
			return { country: '' };
		}
	} );

	const isHdRate = shippingState.rate_id.includes( '-hd' ) && shippingState.belongsToZasilkovna;
	const forceHD      = zasilkovnaAjax?.forceHDChecker === 'ok';
	const hdCountries  = [ 'CZ', 'SK' ];
	const isHdRequired = isHdRate && forceHD && hdCountries.includes( customerState.country );
	const isRequired   = shippingState.hasBranches || isHdRequired;
	const showBlock    = ( shippingState.hasBranches || isHdRate ) && shippingState.belongsToZasilkovna;

	const errorMessage = isHdRate
		? __( 'Please select a delivery address using the Packeta widget.', 'zasilkovna' )
		: __( 'Please select a pick-up point.', 'zasilkovna' );

	useEffect( () => {
		if ( pointData ) setShowError( false );
	}, [ pointData ] );

	useEffect( () => {
		if ( showError && errorRef.current ) {
			errorRef.current.scrollIntoView( { behavior: 'smooth', block: 'center' } );
		}
	}, [ showError ] );

	useEffect( () => {
		if ( ! isRequired || ! showBlock ) return;

		const handlePlaceOrder = ( e ) => {
			if ( ! pointData ) {
				e.preventDefault();
				e.stopPropagation();
				setShowError( true );
			}
		};

		const attachBtn = () => {
			const btn = document.querySelector( '.wc-block-components-checkout-place-order-button' );
			if ( btn && btn !== placeOrderBtnRef.current ) {
				if ( placeOrderBtnRef.current ) {
					placeOrderBtnRef.current.removeEventListener( 'click', handlePlaceOrder, true );
				}
				btn.addEventListener( 'click', handlePlaceOrder, true );
				placeOrderBtnRef.current = btn;
			}
		};

		attachBtn();

		const observer = new MutationObserver( attachBtn );
		observer.observe( document.body, { childList: true, subtree: true } );

		return () => {
			observer.disconnect();
			if ( placeOrderBtnRef.current ) {
				placeOrderBtnRef.current.removeEventListener( 'click', handlePlaceOrder, true );
				placeOrderBtnRef.current = null;
			}
		};
	}, [ isRequired, showBlock, pointData ] );

	useEffect( () => {
		if ( prevRateId.current === null ) {
			prevRateId.current = shippingState.rate_id;
			return;
		}
		if ( prevRateId.current !== shippingState.rate_id ) {
			prevRateId.current = shippingState.rate_id;
			setPointData( '' );
			setSelectedText( '' );
			setShowError( false );
			setExtensionData( 'tzas-block-parcelshop', 'tzas_message', '' );
			setExtensionData( 'tzas-block-parcelshop', 'tzas_data', '' );
			setHasAttemptedOpen( false );
		}
	}, [ shippingState.rate_id ] );

	const openWidget = useCallback( async () => {
		if ( widgetLocked ) return;
		try {
			const cartStore = wp.data.select( 'wc/store/cart' );
			if ( ! cartStore ) return;

			closeWidget();

			const selectedMethodId = getSelectedMethodId( cartStore );

			const cartWeight = await ( async () => {
				try {
					const res = await new Promise( ( resolve, reject ) => {
						jQuery.post( zasilkovnaAjax?.ajaxurl, { action: 'get_cart_weight' } ).done( resolve ).fail( reject );
					} );
					return parseFloat( res ) || 0;
				} catch { return 0; }
			} )();

			const rawKey = await new Promise( ( resolve, reject ) => {
				jQuery.post( zasilkovnaAjax?.ajaxurl, { action: 'toret_get_key', nazev: selectedMethodId } ).done( resolve ).fail( reject );
			} );

			if ( ! rawKey ) throw new Error( 'Empty response' );
			const carrierKey = rawKey.replace( /(\r\n|\n|\r)/gm, '' ).trim();
			const carrierId  = carrierKey === 'paket' ? 'packeta' : carrierKey;

			if ( ! Packeta?.Widget ) throw new Error( 'Widget not available' );

			const customerData    = cartStore.getCustomerData();
			const shippingCountry = customerData?.shippingAddress?.country || '';
			const billingCountry  = customerData?.billingAddress?.country || '';
			const countryLower    = ( shippingCountry || billingCountry || 'CZ' ).toLowerCase();
			const countryUpper    = countryLower.toUpperCase();
			const lang            = document.documentElement.lang.split( '-' )[ 0 ] || 'cs';

			let widgetOptions = { weight: cartWeight, language: lang, appIdentity: zasilkovnaAjax.appIdentity || '' };

			if ( carrierKey === 'packeta-zbox' ) {
				widgetOptions = { ...widgetOptions, country: countryLower, vendors: [ { country: countryLower, group: 'zbox' } ] };
			} else if ( [ 'packeta', 'packeta-zpoints' ].includes( carrierKey ) ) {
				const disableZbox = await new Promise( ( resolve ) => {
					jQuery.ajax( { url: zasilkovnaAjax.ajaxurl, type: 'POST', data: { action: 'get_disabled_zboxes' } } )
						.done( ( res ) => { if ( res?.disableZbox ) zasilkovnaAjax.disableZbox = res.disableZbox; resolve( res?.disableZbox === 'yes' ); } )
						.fail( () => resolve( false ) );
				} );
				widgetOptions = disableZbox || carrierKey === 'packeta-zpoints'
					? { ...widgetOptions, country: countryLower, vendors: [ { country: countryLower } ] }
					: { ...widgetOptions, country: countryLower, carrier: 'packeta' };
			} else {
				widgetOptions = { ...widgetOptions, country: countryLower, carrierId };
				if ( [ 'CZ', 'SK' ].includes( countryUpper ) && selectedMethodId.includes( '-hd' ) ) {
					widgetOptions.layout = 'hd';
				}
			}

			Packeta.Widget.pick( zasilkovnaAjax.apikey, ( point ) => {
				if ( ! point ) return;
				if ( ! point.id && ! point.address ) return;

				let text, data;

				if ( point.address ) {
					const { street, houseNumber, postcode, city } = point.address;
					if ( ! street && ! city ) return;
					text = `${ street } ${ houseNumber }, ${ city }, ${ postcode }`;
					data = JSON.stringify( point );
				} else {
					if ( ! point.name || ! point.street ) return;
					text = `${ point.name }, ${ point.street }, ${ point.city }, ${ point.zip }`;
					data = JSON.stringify( {
						id: point.id, name: point.name, place: point.place, street: point.street,
						city: point.city, zip: point.zip, country: ( point.country || '' ).toUpperCase(),
						url: point.url, gps: point.gps, carrierId: point.carrierId,
						carrierPickupPointId: point.carrierPickupPointId,
					} );
				}

				setPointData( data );
				setSelectedText( text );
				setShowError( false );
				setExtensionData( 'tzas-block-parcelshop', 'tzas_message', data );
				setExtensionData( 'tzas-block-parcelshop', 'tzas_data', text );

				const dispatch = wp.data.dispatch( 'wc/store/cart' );
				if ( dispatch ) {
					if ( point.address ) {
						dispatch.setShippingAddress( {
							address_1: `${ point.address.street } ${ point.address.houseNumber }`,
							address_2: '', postcode: point.address.postcode,
							city: point.address.city, country: ( point.address.country || '' ).toUpperCase(),
						} );
					} else if ( zasilkovnaAjax?.changeShippingAddress === 'ok' ) {
						dispatch.setShippingAddress( {
							address_1: point.street || '', address_2: point.place || '',
							postcode: point.zip || '', city: point.city || '',
							country: ( point.country || '' ).toUpperCase(),
						} );
					}
				}
			}, widgetOptions );

			setWidgetLocked( true );
			setTimeout( () => setWidgetLocked( false ), 1000 );
		} catch ( e ) {}
	}, [ widgetLocked, setPointData, setSelectedText, setExtensionData ] );

	const handleClick = useCallback( ( e ) => { e.preventDefault(); openWidget(); }, [ openWidget ] );

	useEffect( () => {
		if ( ( ! shippingState.hasBranches && ! isHdRate ) || ! shippingState.belongsToZasilkovna ) return;
		if ( zasilkovnaAjax?.showOnSelect !== 'ok' ) return;
		if ( pointData ) return;
		if ( hasAttemptedOpen ) return;
		if ( typeof Packeta === 'undefined' ) return;
		setHasAttemptedOpen( true );
		openWidget();
	}, [ shippingState.rate_id, isHdRate, pointData, hasAttemptedOpen, openWidget ] );

	if ( ! showBlock ) return null;

	const addressLabel = isHdRate
		? __( 'Delivery address', 'zasilkovna' )
		: __( 'Pick-up point address', 'zasilkovna' );

	const buttonLabel = isHdRate
		? __( 'Select delivery address', 'zasilkovna' )
		: __( 'Select pick-up point', 'zasilkovna' );

	return (
		<div className="tzzas-block-parcelshops">
			<AddressDisplay label={ addressLabel } value={ selectedText } />
			<button
				className="button zas-pop-kont"
				id="zas-pop-kont"
				onClick={ handleClick }
				type="button"
			>
				{ buttonLabel }
			</button>
			{ showError && isRequired && ! pointData && (
				<div
					ref={ errorRef }
					className="wc-block-components-validation-error tzas-validation-error"
					role="alert"
				>
					<p>{ errorMessage }</p>
				</div>
			) }
		</div>
	);
};

registerCheckoutBlock( { metadata, component: ParcelshopsComponent } );